// src/Currencies.js
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import './styles/ManagementPages.css';

const currencyFlags = {
  USD: 'us',
  EUR: 'eu',
  GBP: 'gb',
  JPY: 'jp',
  PLN: 'pl',
  CNY: 'cn',
  KRW: 'kr',
  PEN: 'pe',
  ILS: 'il',
  TZS: 'tz',
  KES: 'ke',
  MVR: 'mv',
  LKR: 'lk',
  SCR: 'sc',
  IDR: 'id',
  INR: 'in',
  MUR: 'mu',
  RUB: 'ru',
  SAR: 'sa',
  MYR: 'my',
  QAR: 'qa',
  OMR: 'om',
  IRR: 'ir',
  BRL: 'br',
  ZAR: 'za',
  DOP: 'do',
  PHP: 'ph',
  MXN: 'mx',
  COP: 'co',
  CLP: 'cl',
  ARS: 'ar',
  TRY: 'tr',
  LBP: 'lb',
  EGP: 'eg',
  LTL: 'lt',
  BGN: 'bg',
  RON: 'ro',
  HRK: 'hr',
  UAH: 'ua',
  XPF: 'pf',
  XAF: 'bf',
  XOF: 'sn',
  CHF: 'ch',
  HUF: 'hu',
  VND: 'vn',
  TWD: 'tw',
  SGD: 'sg',
  NZD: 'nz',
  JMD: 'jm',
  HKD: 'hk',
  CAD: 'ca',
  AUD: 'au',
  MAD: 'ma',
  AED: 'ae',
  TND: 'tn',
  RSD: 'rs',
  JOD: 'jo',
  BHD: 'bh',
  DZD: 'dz',
  CZK: 'cz',
  SEK: 'se',
  NOK: 'no',
  ISK: 'is',
  DKK: 'dk',
  CRC: 'cr',
  BOB: 'bo',
  THB: 'th',
  ALL: 'al'
};

const currencyNames = {
  USD: 'Dollar US',
  EUR: 'Euro',
  GBP: 'Livre Sterling',
  JPY: 'Yen Japonais',
  PLN: 'Zloty polonais',
  CNY: 'Yuan chinois',
  KRW: 'Won sud-coréen',
  PEN: 'Sol péruvien',
  ILS: 'Shekel israélien',
  TZS: 'Shilling tanzanien',
  KES: 'Shilling kenyan',
  MVR: 'Rufiyaa maldivien',
  LKR: 'Roupie sri-lankaise',
  SCR: 'Roupie des Seychelles',
  IDR: 'Roupie indonésienne',
  INR: 'Roupie indienne',
  MUR: 'Roupie mauricienne',
  RUB: 'Rouble russe',
  SAR: 'Riyal saoudien',
  MYR: 'Ringgit malaisien',
  QAR: 'Riyal qatari',
  OMR: 'Rial omanais',
  IRR: 'Rial iranien',
  BRL: 'Réal brésilien',
  ZAR: 'Rand sud-africain',
  DOP: 'Peso dominicain',
  PHP: 'Peso philippin',
  MXN: 'Peso mexicain',
  COP: 'Peso colombien',
  CLP: 'Peso chilien',
  ARS: 'Peso argentin',
  TRY: 'Livre turque',
  LBP: 'Livre libanaise',
  EGP: 'Livre égyptienne',
  LTL: 'Litas lituanien',
  BGN: 'Lev bulgare',
  RON: 'Leu roumain',
  HRK: 'Kuna croate',
  UAH: 'Hryvnia ukrainienne',
  XPF: 'Franc CFP',
  XAF: 'Franc CFA (BEAC)',
  XOF: 'Franc CFA (BCEAO)',
  CHF: 'Franc suisse',
  HUF: 'Forint hongrois',
  VND: 'Dong vietnamien',
  TWD: 'Dollar taïwanais',
  SGD: 'Dollar de Singapour',
  NZD: 'Dollar néo-zélandais',
  JMD: 'Dollar jamaïcain',
  HKD: 'Dollar de Hong Kong',
  CAD: 'Dollar canadien',
  AUD: 'Dollar australien',
  MAD: 'Dirham marocain',
  AED: 'Dirham des Émirats arabes unis',
  TND: 'Dinar tunisien',
  RSD: 'Dinar serbe',
  JOD: 'Dinar jordanien',
  BHD: 'Dinar bahreïni',
  DZD: 'Dinar algérien',
  CZK: 'Couronne tchèque',
  SEK: 'Couronne suédoise',
  NOK: 'Couronne norvégienne',
  ISK: 'Couronne islandaise',
  DKK: 'Couronne danoise',
  CRC: 'Colón costaricain',
  BOB: 'Boliviano',
  THB: 'Baht thaïlandais',
  ALL: 'Lek albanais'
};

const formatNumber = (value, options = {}) => {
  if (value === null || value === undefined || value === '') return '—';
  const numeric = Number(value);
  if (Number.isNaN(numeric)) return value;
  return new Intl.NumberFormat('fr-FR', {
    minimumFractionDigits: 4,
    maximumFractionDigits: 4,
    ...options,
  }).format(numeric);
};

const Currencies = () => {
  const [currencies, setCurrencies] = useState([]);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const authToken = localStorage.getItem('token');

  const fetchCurrencies = useCallback(async () => {
    setError('');
    try {
      const response = await axios.get('/api/currencies', {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      setCurrencies(response.data);
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.error || 'Erreur lors de la récupération des devises');
    }
  }, [authToken]);

  useEffect(() => {
    fetchCurrencies();
  }, [fetchCurrencies]);

  const filteredCurrencies = useMemo(() => {
    const lowerTerm = searchTerm.trim().toLowerCase();
    if (!lowerTerm) return currencies;
    return currencies.filter((curr) => {
      const code = curr.to_currency?.toLowerCase() || '';
      const name = currencyNames[curr.to_currency]?.toLowerCase() || '';
      return (
        code.includes(lowerTerm) ||
        String(curr.id).includes(lowerTerm) ||
        name.includes(lowerTerm)
      );
    });
  }, [currencies, searchTerm]);

  const stats = useMemo(() => {
    const total = currencies.length;
    const filtered = filteredCurrencies.length;
    const fastestDelay =
      filteredCurrencies.length > 0
        ? Math.min(...filteredCurrencies.map((curr) => Number(curr.delay) || Infinity))
        : null;
    const averageRate =
      filteredCurrencies.length > 0
        ? filteredCurrencies.reduce((acc, curr) => acc + (Number(curr.rate) || 0), 0) /
          filteredCurrencies.length
        : 0;

    return {
      total,
      filtered,
      fastestDelay: Number.isFinite(fastestDelay) ? fastestDelay : null,
      averageRate,
    };
  }, [currencies, filteredCurrencies]);

  return (
    <div className="management-page">
      <div className="management-page__inner">
        <header className="management-header glass-panel">
          <div className="management-header__content">
            <h1 className="management-title">
              <span className="management-title__icon">💱</span>
              Devises internationales
            </h1>
            <p className="management-subtitle">
              Consultez les taux de change mis à jour en continu pour piloter vos opérations
              multi-devises en toute confiance.
            </p>
            <div className="management-meta">
              <span className="management-badge">Taux temps réel</span>
              <span className="management-badge">Données Xignite</span>
            </div>
          </div>
          <div className="management-header__actions">
            <button type="button" className="mgmt-btn mgmt-btn--outline" onClick={fetchCurrencies}>
              Rafraîchir
            </button>
          </div>
        </header>

        <section className="management-stats">
          <div className="management-stat-card glass-panel">
            <span className="management-stat-title">Paires suivies</span>
            <span className="management-stat-value">{stats.total}</span>
            <span className="management-stat-meta">Base EUR</span>
          </div>
          <div className="management-stat-card glass-panel">
            <span className="management-stat-title">Résultats filtrés</span>
            <span className="management-stat-value">{stats.filtered}</span>
            <span className="management-stat-meta">Recherche active</span>
          </div>
          <div className="management-stat-card glass-panel">
            <span className="management-stat-title">Taux moyen</span>
            <span className="management-stat-value">{formatNumber(stats.averageRate)}</span>
            <span className="management-stat-meta">Sur la sélection</span>
          </div>
          <div className="management-stat-card glass-panel">
            <span className="management-stat-title">Latence la plus basse</span>
            <span className="management-stat-value">
              {stats.fastestDelay !== null ? `${stats.fastestDelay} s` : 'n/a'}
            </span>
            <span className="management-stat-meta">Réactivité API</span>
          </div>
        </section>

        {error && (
          <div className="management-alert error">
            <span>⚠️</span>
            {error}
          </div>
        )}

        <section className="management-section glass-panel">
          <div className="management-section__head">
            <h2 className="management-section__title">Filtrer les devises</h2>
            <p className="management-section__subtitle">
              Recherchez par code ISO (USD, GBP), par nom complet ou par identifiant interne.
            </p>
          </div>
          <div className="management-form__field">
            <label htmlFor="currency-search" className="management-form__label">
              Recherche
            </label>
            <input
              id="currency-search"
              className="mgmt-input"
              type="text"
              placeholder="Ex : USD, Dollar, 42"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
            />
          </div>
        </section>

        <section className="management-section glass-panel">
          <div className="management-section__head">
            <h2 className="management-section__title">Taux de change</h2>
            <p className="management-section__subtitle">
              {filteredCurrencies.length > 0
                ? `1 EUR = ${formatNumber(filteredCurrencies[0].rate)} ${
                    filteredCurrencies[0].to_currency
                  }`
                : 'Sélectionnez une devise pour voir le détail.'}
            </p>
          </div>

          {filteredCurrencies.length === 0 ? (
            <div className="management-empty-state">
              <span className="management-empty-state__icon">🌍</span>
              <span className="management-empty-state__title">Aucune devise trouvée</span>
              <span className="management-empty-state__subtitle">
                Ajustez votre recherche ou effacez le champ pour afficher la liste complète.
              </span>
            </div>
          ) : (
            <div className="management-cards-grid">
              {filteredCurrencies.map((currency) => {
                const flagCode =
                  currencyFlags[currency.to_currency] || currency.to_currency?.toLowerCase();
                const currencyName = currencyNames[currency.to_currency] || currency.to_currency;
                const lastUpdate = currency.timestamp
                  ? new Date(currency.timestamp).toLocaleString('fr-FR')
                  : 'n/a';
                return (
                  <article key={currency.id} className="management-currency-card glass-panel">
                    <div className="management-currency-card__header">
                      <div className="management-currency-card__title">
                        <h3>{currencyName}</h3>
                        <span>{currency.to_currency}</span>
                      </div>
                      <div className="management-currency-flag">
                        <span className={`flag-icon flag-icon-${flagCode}`} title={currencyName} />
                      </div>
                    </div>

                    <div className="management-currency-card__metrics">
                      <div className="management-metric">
                        <span className="management-metric__label">Taux direct</span>
                        <span className="management-metric__value">
                          {formatNumber(currency.rate)}
                        </span>
                      </div>
                      <div className="management-metric">
                        <span className="management-metric__label">Inverse</span>
                        <span className="management-metric__value">
                          {formatNumber(currency.inverse)}
                        </span>
                      </div>
                      <div className="management-metric">
                        <span className="management-metric__label">Résultat</span>
                        <span className="management-metric__value">
                          {formatNumber(currency.result, { maximumFractionDigits: 6 })}
                        </span>
                      </div>
                      <div className="management-metric">
                        <span className="management-metric__label">Latence</span>
                        <span className="management-metric__value">
                          {currency.delay ? `${currency.delay} s` : 'n/a'}
                        </span>
                      </div>
                    </div>

                    <div className="management-currency-card__footer">
                      <span>ID #{currency.id}</span>
                      <span>MAJ : {lastUpdate}</span>
                    </div>

                    <div className="management-summary">
                      <strong>1 EUR</strong> = {formatNumber(currency.rate)} {currency.to_currency}
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default Currencies;